import React, { useState } from "react";
import { Landmark, Banknote, CreditCard, Smartphone, ChevronRight } from "lucide-react";
import ReceivingAccountsOverlay from "./ReceivingAccountsOverlay";
import AddReceivingAccountOverlay from "./AddReceivingAccountOverlay";
import "./PaymentAccountsSection.css";

// paymentType -> { icon, label }
const TYPE_META = {
  cash: { icon: Banknote, label: "Cash" },
  POS: { icon: CreditCard, label: "POS Terminal" },
  bankTransfer: { icon: Landmark, label: "Bank Transfer" },
  mobilePay: { icon: Smartphone, label: "Mobile Pay" },
  card: { icon: CreditCard, label: "Card" },
  storelensePOS: { icon: CreditCard, label: "StoreLense POS" },
};

function getAccountDetail(account) {
  if (!account) return "";
  switch (account.paymentType) {
    case "bankTransfer":
      return account.accountNumber ? `${account.bankName || "Bank"} • •••• ${account.accountNumber.slice(-4)}` : account.bankName || "";
    case "POS":
      return account.terminalId ? `Terminal ${account.terminalId}` : account.provider || "";
    case "mobilePay":
      return account.walletNumber || account.provider || "";
    case "cash":
      return account.provider || "Active";
    default:
      return account.provider || "";
  }
}

/**
 * Drop-in section: renders the "Receiving Account" dashboard card plus the
 * accounts list overlay and add/edit overlay. Wire up the callback props to
 * your API — this component holds only UI state (which overlay is open).
 *
 * Props:
 *   accounts        — array of ReceivingAccount documents
 *   onAddAccount     — (payload) => Promise
 *   onUpdateAccount  — (id, payload) => Promise
 *   onDeleteAccount  — (id) => Promise
 *   loading          — boolean, optional
 */
export default function PaymentAccountsSection({ accounts = [], onAddAccount, onUpdateAccount, onDeleteAccount, loading = false }) {
  const [listOpen, setListOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const currentAccount = accounts.find((a) => a.active !== false) || accounts[0] || null;
  const meta = currentAccount ? TYPE_META[currentAccount.paymentType] : null;
  const Icon = meta?.icon || Landmark;

  const openAddForm = () => {
    setEditingAccount(null);
    setFormOpen(true);
  };

  const openEditForm = (account) => {
    setEditingAccount(account);
    setFormOpen(true);
  };

  const handleSave = async (payload) => {
    console.log(payload);

    if (editingAccount) {
      await onUpdateAccount?.(editingAccount._id, payload);
    } else {
      await onAddAccount?.(payload);
    }
    setFormOpen(false);
    setEditingAccount(null);
  };

  return (
    <>
      <section className='pas-card'>
        <div className='pas-card__header'>
          <div className='pas-card__title-group'>
            <span className='pas-card__icon'>
              <Landmark size={18} strokeWidth={2.25} />
            </span>
            <h3 className='pas-card__title'>Receiving Account</h3>
          </div>
          <button type='button' className='pas-card__manage-btn' onClick={() => setListOpen(true)}>
            View Receiving Accounts
            <ChevronRight size={16} strokeWidth={2.25} />
          </button>
        </div>

        {loading ? (
          <div className='pas-card__skeleton' />
        ) : currentAccount ? (
          <div className='pas-card__current'>
            <span className={`pas-card__current-icon pas-card__current-icon--${currentAccount.paymentType}`}>
              <Icon size={20} strokeWidth={2.25} />
            </span>
            <div className='pas-card__current-info'>
              <span className='pas-card__current-name'>{currentAccount.name}</span>
              <span className='pas-card__current-detail'>{getAccountDetail(currentAccount)}</span>
            </div>
            <span className='pas-card__badge'>{meta?.label}</span>
          </div>
        ) : (
          <div className='pas-card__empty'>
            <p className='pas-card__empty-text'>No receiving account set up yet.</p>
            <button type='button' className='pas-card__empty-btn' onClick={openAddForm}>
              + Add Payment Account
            </button>
          </div>
        )}
      </section>

      {listOpen && (
        <ReceivingAccountsOverlay accounts={accounts} onClose={() => setListOpen(false)} onAdd={openAddForm} onEdit={openEditForm} onDelete={onDeleteAccount} />
      )}

      {formOpen && (
        <AddReceivingAccountOverlay
          initialAccount={editingAccount}
          onClose={() => {
            setFormOpen(false);
            setEditingAccount(null);
          }}
          onSave={async (payload) => {
            await handleSave(payload);
          }}
        />
      )}
    </>
  );
}
